import {
  clearPendingAutoReload,
  getLastAutoReloadAt,
  setLastAutoReloadAt,
  setPendingAutoReloadUntil,
} from '@/lib/autoReload'
import type { Toast, ToastAction, ToastType } from '@/components/providers/ToastProvider'

export type ReloadReason =
  | 'recovery-failure'
  | 'sw-update'
  | 'save-failure'
  | 'background-health-failure'

export interface ToastApi {
  showToast: (
    message: string,
    type?: ToastType,
    duration?: number,
    action?: ToastAction,
  ) => string
  updateToast: (id: string, updates: Partial<Omit<Toast, 'id'>>) => void
  removeToast: (id: string) => void
}

export interface StartAutoReloadOptions {
  reason: ReloadReason
  message: string
  toast: ToastApi
  countdownSec?: number
  canCancel?: boolean
  toastType?: ToastType
  precondition?: () => Promise<boolean> | boolean
  onCancel?: () => void
  performReload?: () => void
  rateLimitMs?: number
}

export interface AutoReloadHandle {
  cancel: () => void
}

const DEFAULT_COUNTDOWN_SEC = 5
const DEFAULT_RATE_LIMIT_MS = 15_000
const IMMEDIATE_RELOAD_RATE_LIMIT_MS = 10_000

let activeHandle: AutoReloadHandle | null = null

export function isAutoReloadActive(): boolean {
  return activeHandle !== null
}

export function cancelActiveAutoReload(): void {
  activeHandle?.cancel()
}

export function resetAutoReloadForTests(): void {
  activeHandle = null
}

function rateLimitAllows(rateLimitMs: number): boolean {
  const lastAt = getLastAutoReloadAt()
  return !(typeof lastAt === 'number' && Date.now() - lastAt < rateLimitMs)
}

export function triggerImmediateReload(opts?: {
  rateLimitMs?: number
  performReload?: () => void
}): boolean {
  const rateLimitMs = opts?.rateLimitMs ?? IMMEDIATE_RELOAD_RATE_LIMIT_MS
  if (!rateLimitAllows(rateLimitMs)) return false
  setLastAutoReloadAt(Date.now())
  const reload = opts?.performReload ?? (() => window.location.reload())
  reload()
  return true
}

export async function startAutoReload(
  opts: StartAutoReloadOptions,
): Promise<AutoReloadHandle | null> {
  if (activeHandle) return activeHandle

  const rateLimitMs = opts.rateLimitMs ?? DEFAULT_RATE_LIMIT_MS
  if (!rateLimitAllows(rateLimitMs)) return null

  if (opts.precondition) {
    try {
      const proceed = await opts.precondition()
      if (!proceed) return null
    } catch {
      return null
    }
  }

  // Re-check: another caller may have grabbed the slot while the precondition was awaiting.
  if (activeHandle) return activeHandle

  const countdownSec = opts.countdownSec ?? DEFAULT_COUNTDOWN_SEC
  const toastType = opts.toastType ?? 'warning'
  let remaining = countdownSec
  let intervalId: number | null = null
  let toastId: string | null = null
  let settled = false

  const formatMessage = (secs: number) => opts.message.replace('{s}', String(secs))

  const cleanup = () => {
    if (intervalId !== null) {
      window.clearInterval(intervalId)
      intervalId = null
    }
    if (toastId !== null) {
      opts.toast.removeToast(toastId)
      toastId = null
    }
    clearPendingAutoReload()
    if (activeHandle === handle) {
      activeHandle = null
    }
    settled = true
  }

  const cancel = () => {
    if (settled) return
    cleanup()
    opts.onCancel?.()
  }

  setPendingAutoReloadUntil(Date.now() + countdownSec * 1000 + 500)

  const action: ToastAction | undefined =
    opts.canCancel === false ? undefined : { label: 'Cancel refresh', onClick: () => cancel() }

  toastId = opts.toast.showToast(formatMessage(remaining), toastType, 0, action)

  const handle: AutoReloadHandle = { cancel }
  activeHandle = handle

  intervalId = window.setInterval(() => {
    remaining -= 1
    if (remaining <= 0) {
      setLastAutoReloadAt(Date.now())
      const reload = opts.performReload ?? (() => window.location.reload())
      cleanup()
      reload()
      return
    }
    if (toastId !== null) {
      opts.toast.updateToast(toastId, { message: formatMessage(remaining) })
    }
  }, 1000)

  return handle
}
